import { WebSocketArenaTransport } from "../adapters/WebSocketArenaTransport";
import { watchMarketStream } from "./MarketStream";
import { rewardTransferRequestPath } from "../domain/rewardTransfers";
import { applyPublishedGameConfig } from "../adapters/PublishedGameConfig";
import { IndexedDbTapOutboxRepository } from "../adapters/IndexedDbTapOutbox";
import { TapDraftJournal } from "../adapters/TapDraftJournal";
import { ExclusiveTapSession } from "../adapters/ExclusiveTapSession";
import type { SeasonLeaderboard, SupportRequest, SupportReceipt, SupportInbox, SupportThread } from "../domain/community";
import type { GameAnalyticsEvent } from "../domain/analytics";
import { GAME_CONFIG } from "../config/gameConfig";
import { tapBoostAt } from "../domain/tapBoost";
import { GameServiceError } from "../domain/errors";
import { reconcileOnboardingProgress } from "../domain/onboarding";
import type { GameServicePort } from "../domain/ports";
import {
  pendingTapSummary,
  type TapOutboxBatch,
  type TapOutboxRepository,
  type TapOutboxState,
} from "../domain/tapOutbox";
import type {
  ArenaClientCommand,
  ArenaCommandDisposition,
  ArenaProblemEvent,
  ArenaRealtimeConnection,
  ArenaRealtimePort,
  ArenaServerEvent,
} from "../domain/arenaProtocol";
import type {
  AchievementId,
  ArenaGameId,
  ClubRosterResult,
  DevScenarioId,
  DiamondSkinId,
  EnergyRefillResult,
  GameState,
  LoadResult,
  OnboardingAction,
  PremiumOfferId,
  PvpMatchState,
  SeasonTaskCode,
  ServiceEnvelope,
  TapResult,
  UpgradeCode,
  UpgradeResult,
  UserSettings,
} from "../domain/types";
import {
  requestTelegramWriteAccess,
  type TelegramWriteAccessBridge,
  type TelegramWriteAccessChoice,
} from "./telegramWriteAccess";

type TelegramInvoiceStatus = "paid" | "cancelled" | "failed" | "pending";

export type TelegramGameBridge = TelegramWriteAccessBridge & {
  initData: string;
  initDataUnsafe?: { start_param?: unknown; user?: { id?: unknown } };
  openInvoice?(url: string, callback: (status: TelegramInvoiceStatus) => void): void;
};

type TelegramWindow = Window & { Telegram?: { WebApp?: TelegramGameBridge } };

type ApiEnvelope = {
  serverNow: string;
  state?: unknown;
  config?: unknown;
  receipt?: unknown;
  responseMode?: unknown;
  stateRevision?: unknown;
  tapState?: unknown;
  offlineGain?: unknown;
  offlineSeconds?: unknown;
  offlineReserveUsedSeconds?: unknown;
  nextRecommendedSyncAt?: unknown;
};

type ApiTapDeltaState = {
  stateRevision: string;
  available: string;
  reserved: string;
  energyCurrent: number;
  energyRemainderMs: string;
  lastEnergyAt: string;
  confirmedTaps: number;
  primaryProduced: string;
  firstTapHintSeen?: true;
};

type ApiErrorPayload = {
  error?: string | {
    code?: unknown;
    messageKey?: unknown;
    details?: unknown;
    retryable?: unknown;
  };
};

type PremiumOfferResponse = {
  offers?: unknown;
};

type InvoiceResponse = {
  invoiceId?: unknown;
  invoiceLink?: unknown;
  status?: unknown;
};

type PremiumPurchaseResponse = {
  status?: unknown;
  purchaseId?: unknown;
};

type TelegramWriteAccessResponse = {
  status?: unknown;
};

type ClubRosterResponse = {
  serverNow?: unknown;
  friends?: unknown;
  clubRoster?: unknown;
  referrals?: unknown;
};

type HttpGameServiceOptions = {
  apiOrigin?: string;
  fetchImpl?: typeof fetch;
  telegram?: TelegramGameBridge | null;
  arenaRealtime?: ArenaRealtimePort | null;
  now?: () => number;
  wait?: (milliseconds: number) => Promise<void>;
  tapOutbox?: TapOutboxRepository;
  onRequestMetric?: (metric: {
    route: string;
    method: string;
    durationMs: number;
    status: number;
    ok: boolean;
    apiCode?: string;
    requestId?: string;
    reason?: string;
  }) => void;
};

const API_PREFIX = "/api/diamond-tapper/v1";
const PAYMENT_POLL_MS = 500;
const PAYMENT_POLL_ATTEMPTS = 40;
const ARENA_COMMAND_TIMEOUT_MS = 8_000;
const TAP_RETRY_BASE_MS = 750;
const TAP_RETRY_MAX_MS = 30_000;
const TAP_REQUEST_TIMEOUT_MS = 8_000;
const RETRYABLE_HTTP_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

type TapWaiter = {
  resolve(result: TapResult): void;
  reject(error: unknown): void;
};

type ArenaPendingCommand = {
  commandId: string;
  dispatched: boolean;
  accepts(match: PvpMatchState): boolean;
  resolve(envelope: ServiceEnvelope): void;
  reject(error: GameServiceError): void;
  timeoutId: ReturnType<typeof setTimeout>;
};

type ArenaActionCommand = Exclude<ArenaClientCommand, { type: "arena.resume" }>;

function analyticsRoute(path: string, origin: string): string {
  const pathname = new URL(path, origin).pathname;
  return pathname
    .replace(/\/(allocations|achievements|season-tasks)\/[^/]+/g, "/$1/:id")
    .replace(/\/premium\/offers\/[^/]+/g, "/premium/offers/:id")
    .replace(/\/(market\/(?:swaps|tasks|slots|positions|liquidity)|support\/tickets)\/(?!quote(?:\/|$)|prepare(?:\/|$))[^/]+(?=\/(?:submit|claim|withdraw|wallet-request|messages|read)|$)/g, "/$1/:id");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown, name: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`Invalid ${name}`);
  return value;
}

function integerValue(value: unknown, fallback = 0): number {
  return Number.isSafeInteger(value) ? (value as number) : fallback;
}

function parseState(value: unknown): GameState {
  if (!isRecord(value) || value.schemaVersion !== 7 || !Number.isSafeInteger(value.revision)) {
    throw new Error("Unsupported Diamond Tapper state contract");
  }
  const account = value.account;
  const season = value.season;
  const energy = value.energy;
  const progress = value.progress;
  const pvp = value.pvp;
  const settings = value.settings;
  if (
    !isRecord(account) ||
    typeof account.available !== "string" ||
    typeof account.reserved !== "string" ||
    !isRecord(season) ||
    typeof season.id !== "string" ||
    typeof season.code !== "string" ||
    (season.allocationId !== null && typeof season.allocationId !== "string") ||
    !isRecord(energy) ||
    !Number.isSafeInteger(energy.current) ||
    !isRecord(progress) ||
    !isRecord(pvp) ||
    typeof pvp.status !== "string" ||
    !isRecord(settings)
  ) {
    throw new Error("Incomplete Diamond Tapper state contract");
  }
  if (!isRecord(value.clubRoster)) {
    if (!Array.isArray(value.friends)) throw new Error("Incomplete Diamond Tapper club contract");
    const friends = value.friends.filter(isRecord);
    value.clubRoster = {
      page: 1,
      pageSize: Math.max(1, friends.length),
      totalPages: 1,
      filteredCount: friends.length,
      networkCount: friends.length,
      nearCount: friends.filter((friend) => friend.line === 1).length,
      farCount: friends.filter((friend) => friend.line === 2).length,
      query: "",
      line: null,
    };
  }
  if (!Array.isArray(value.seasonClaims)) value.seasonClaims = [];
  if (!isRecord(value.tonUsdtPrize)) {
    value.tonUsdtPrize = { campaign: null, award: null };
  }
  if (typeof progress.primaryProduced !== "string") progress.primaryProduced = "0";
  const daily = isRecord(progress.daily) ? progress.daily : null;
  const seasonal = isRecord(progress.seasonal) ? progress.seasonal : null;
  if (daily !== null) {
    if (!Number.isSafeInteger(daily.energySpent)) daily.energySpent = integerValue(daily.confirmedTaps) * GAME_CONFIG.energy.costPerTap;
    if (!Number.isSafeInteger(daily.offlineCollections)) daily.offlineCollections = 0;
    if (!Number.isSafeInteger(daily.returnVisits)) daily.returnVisits = 0;
    if (!Number.isSafeInteger(daily.arenaWins)) daily.arenaWins = 0;
    if (!Number.isSafeInteger(daily.pvpMatches)) daily.pvpMatches = 0;
    if (!Number.isSafeInteger(daily.pvpWins)) daily.pvpWins = 0;
  }
  if (seasonal !== null) {
    if (!Number.isSafeInteger(seasonal.arenaWins)) seasonal.arenaWins = 0;
    if (!Number.isSafeInteger(seasonal.pvpMatches)) seasonal.pvpMatches = 0;
    if (!Number.isSafeInteger(seasonal.pvpWins)) seasonal.pvpWins = 0;
    if (!Number.isSafeInteger(seasonal.activeInvites)) seasonal.activeInvites = 0;
  }
  if (!isRecord(season.communityBonus)) {
    season.communityBonus = {
      basisPoints: GAME_CONFIG.community.finalBalanceBonusBasisPoints,
      status: "pending",
      previewAmount: "0",
      appliedAmount: "0",
      checkedAt: null,
    };
  }
  const telegram = isRecord(value.telegram) ? value.telegram : null;
  for (const [target, url] of [
    ["channel", GAME_CONFIG.community.channelUrl],
    ["chat", GAME_CONFIG.community.chatUrl],
  ] as const) {
    const membership = telegram !== null && isRecord(telegram[target]) ? telegram[target] : null;
    if (membership !== null) {
      if (typeof membership.available !== "boolean") membership.available = true;
      if (typeof membership.joinUrl !== "string") membership.joinUrl = url;
    }
  }
  const meta = isRecord(value.meta) ? value.meta : null;
  if (meta !== null) {
    if (typeof meta.lastSessionAt !== "string") meta.lastSessionAt = meta.lastProductionAt;
    if (typeof meta.lastQualifyingReturnAt !== "string") meta.lastQualifyingReturnAt = null;
  }
  if (isRecord(season.projection)) {
    if (typeof season.projection.visible !== "boolean") season.projection.visible = true;
    if (season.projection.revealsAt !== null && typeof season.projection.revealsAt !== "string") {
      season.projection.revealsAt = null;
    }
  }
  return value as unknown as GameState;
}

function commandDetails(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, typeof item === "string" ? item : String(item)]),
  );
}

function serviceError(payload: ApiErrorPayload, status: number): GameServiceError {
  const raw = payload.error;
  const code = isRecord(raw) && typeof raw.code === "string" ? raw.code : typeof raw === "string" ? raw : "";
  const details = isRecord(raw) ? commandDetails(raw.details) : {};
  const retryable = (isRecord(raw) && raw.retryable === true) || RETRYABLE_HTTP_STATUSES.has(status);
  if (retryable) return new GameServiceError("NETWORK", code || `HTTP_${status}`, { ...details, status: String(status) });
  if (code === "INSUFFICIENT_GAME_UNIT" || code === "INSUFFICIENT_FUNDS") {
    return new GameServiceError("INSUFFICIENT_FUNDS", code, details);
  }
  if (
    code === "SEASON_NOT_ACTIVE" ||
    code === "SEASON_FROZEN" ||
    code === "SEASON_SETTLING" ||
    code === "ACTIVE_SEASON_NOT_FOUND" ||
    code === "diamond_tapper_active_season_not_found" ||
    code === "CLAIM_WINDOW_CLOSED"
  ) {
    return new GameServiceError("SEASON_INACTIVE", code, details);
  }
  if (code === "ENERGY_FULL") return new GameServiceError("ENERGY_FULL", code, details);
  if (code === "REFILL_LIMIT_REACHED") return new GameServiceError("REFILL_LIMIT", code, details);
  return new GameServiceError("INVALID_ACTION", code || `HTTP_${status}`, details);
}

function arenaProblemError(event: ArenaProblemEvent): GameServiceError {
  if (event.code === "INSUFFICIENT_FUNDS") {
    return new GameServiceError("INSUFFICIENT_FUNDS", event.code);
  }
  return new GameServiceError("ARENA_SYNC", event.code);
}

function newSessionId(prefix: string): string {
  const suffix = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${suffix}`;
}

function telegramOwnerKey(telegram: TelegramGameBridge | null): string {
  const unsafeId = telegram?.initDataUnsafe?.user?.id;
  if ((typeof unsafeId === "number" && Number.isSafeInteger(unsafeId)) || typeof unsafeId === "string") {
    return `telegram:${String(unsafeId)}`;
  }
  const encodedUser = telegram === null ? null : new URLSearchParams(telegram.initData).get("user");
  if (encodedUser !== null) {
    try {
      const user = JSON.parse(encodedUser) as { id?: unknown };
      if ((typeof user.id === "number" && Number.isSafeInteger(user.id)) || typeof user.id === "string") {
        return `telegram:${String(user.id)}`;
      }
    } catch {
      // The backend still validates initData. This fallback only scopes local
      // retry state when a test bridge omits Telegram's decoded user object.
    }
  }
  let hash = 2_166_136_261;
  for (const character of telegram?.initData ?? "anonymous") {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16_777_619);
  }
  return `bridge:${(hash >>> 0).toString(36)}`;
}

function deterministicTapRetryDelay(batch: TapOutboxBatch): number {
  const exponent = Math.min(6, Math.max(0, batch.deliveryAttempts - 1));
  const base = Math.min(TAP_RETRY_MAX_MS, TAP_RETRY_BASE_MS * 2 ** exponent);
  let hash = batch.deliveryAttempts;
  for (const character of batch.rootCommandId) hash = Math.imul(hash ^ character.charCodeAt(0), 16_777_619);
  const jitterBasisPoints = 8_000 + (Math.abs(hash) % 4_001);
  return Math.max(TAP_RETRY_BASE_MS, Math.round((base * jitterBasisPoints) / 10_000));
}

function websocketEndpoint(origin: string): string {
  const endpoint = new URL(`${API_PREFIX}/arena/ws`, origin);
  endpoint.protocol = endpoint.protocol === "https:" ? "wss:" : "ws:";
  return endpoint.toString();
}

function defaultWait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export class HttpGameService implements GameServicePort {
  readonly vault = {
    read: (): Promise<unknown> => this.#requestJson<unknown>(`${API_PREFIX}/vault/overview`, { method: "GET" }),
    watchOverview: (onChange: () => void, onState: (state: "live" | "reconnecting") => void) =>
      watchMarketStream(async signal => {
        const get = () => this.#fetch(new URL(`${API_PREFIX}/vault/overview?events=1`, this.#origin), {
          headers: { Authorization: `Bearer ${this.#accessToken}`, Accept: "text/event-stream" }, signal, cache: "no-store",
        });
        const response = await get();
        if (response.status !== 401 || signal.aborted || this.#destroyed) return response;
        await response.body?.cancel(); await this.#authenticate(); return get();
      }, onChange, onState, "vault"),
    startPurchase: (requestId: string, planId: number): Promise<unknown> =>
      this.#requestJson<unknown>(`${API_PREFIX}/vault/purchases`, {
        method: "POST", body: JSON.stringify({ requestId, planId }),
      }),
    purchaseStatus: (requestId: string): Promise<unknown> => {
      if (!/^[A-Za-z0-9:_-]{8,100}$/.test(requestId)) return Promise.reject(new Error("vault_request_id_invalid"));
      return this.#requestJson<unknown>(`${API_PREFIX}/vault/purchases/${encodeURIComponent(requestId)}`, { method: "GET" });
    },
    watchPurchase: (requestId: string, onChange: () => void, onState: (state: "live" | "reconnecting") => void) => {
      if (!/^[A-Za-z0-9:_-]{8,100}$/.test(requestId)) throw new Error("vault_request_id_invalid");
      return watchMarketStream(async signal => {
        const get = () => this.#fetch(new URL(`${API_PREFIX}/vault/purchases/${encodeURIComponent(requestId)}/events`, this.#origin), {
          headers: { Authorization: `Bearer ${this.#accessToken}`, Accept: "text/event-stream" }, signal, cache: "no-store",
        });
        const response = await get();
        if (response.status !== 401 || signal.aborted || this.#destroyed) return response;
        await response.body?.cancel(); await this.#authenticate(); return get();
      }, onChange, onState, "vault");
    },
  };
  readonly transfers = {
    request: <T>(path: string, body?: unknown): Promise<T> => {
      const suffix = rewardTransferRequestPath(path);
      return this.#requestJson<T>(`${API_PREFIX}/reward-transfers${suffix}`, {
        method: body === undefined ? "GET" : "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    },
    watch: (onChange: () => void, onState: (state: "live" | "reconnecting") => void) => watchMarketStream(async signal => {
      const get = () => this.#fetch(new URL(`${API_PREFIX}/reward-transfers/stream`, this.#origin), {
        headers: { Authorization: `Bearer ${this.#accessToken}`, Accept: "text/event-stream" }, signal, cache: "no-store",
      });
      const response = await get();
      if (response.status !== 401 || signal.aborted || this.#destroyed) return response;
      await response.body?.cancel(); await this.#authenticate(); return get();
    }, onChange, onState, "transfers"),
  };
  readonly market = {
    request: <T>(path: string, body?: unknown): Promise<T> => {
      if (!/^\/(?:[a-zA-Z0-9_/?=&%:.+-]*)$/.test(path) || path.includes("..")) return Promise.reject(new Error("market_path_invalid"));
      return this.#requestJson<T>(`${API_PREFIX}/market${path === "/" ? "" : path}`, {
        method: body === undefined ? "GET" : "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    },
    watch: (onChange: () => void, onState: (state: "live" | "reconnecting") => void) => watchMarketStream(async signal => {
      const get = () => this.#fetch(new URL(`${API_PREFIX}/market/stream`, this.#origin), {
        headers: { Authorization: `Bearer ${this.#accessToken}`, Accept: "text/event-stream" }, signal, cache: "no-store",
      });
      const response = await get();
      if (response.status !== 401 || signal.aborted || this.#destroyed) return response;
      await response.body?.cancel();
      await this.#authenticate();
      return get();
    }, onChange, onState),
  };
  readonly #origin: string;
  readonly #fetch: typeof fetch;
  readonly #telegram: TelegramGameBridge | null;
  readonly #arenaRealtime: ArenaRealtimePort;
  readonly #now: () => number;
  readonly #wait: (milliseconds: number) => Promise<void>;
  readonly #tapOutboxRepository: TapOutboxRepository;
  readonly #tapOutboxOwnerKey: string;
  readonly #onRequestMetric: HttpGameServiceOptions["onRequestMetric"];
  readonly #listeners = new Set<(envelope: ServiceEnvelope) => void>();
  readonly #offerVersions = new Map<PremiumOfferId, string>();
  #premiumOfferStates: { seasonId: string; offers: NonNullable<GameState["premium"]["offerStates"]> } | null = null;
  #premiumCatalogSequence = 0;
  #accessToken = "";
  #state: GameState | null = null;
  #serverNow = new Date(0).toISOString();
  #serverAnchor = 0;
  #nextSyncAt = 0;
  #online = true;
  #destroyed = false;
  #inviteUrl = "";
  #recentRequestErrors: import("../domain/analytics").GameAnalyticsEvent[] = [];
  #commandSessionId = newSessionId("command-session");
  #tapOutboxScope = "";
  readonly #tapDrafts = new TapDraftJournal();
  readonly #exclusiveSession = new ExclusiveTapSession();
  #tapOutboxState: TapOutboxState | null = null;
  #tapOutboxWriteTail: Promise<void> = Promise.resolve();
  #tapDrainPromise: Promise<void> | null = null;
  readonly #tapWaiters = new Map<string, TapWaiter>();
  #syncPromise: Promise<ServiceEnvelope> | null = null;
  readonly #rewardClaims = new Map<string, Promise<ServiceEnvelope>>();
  #arenaConnection: ArenaRealtimeConnection | null = null;
  #arenaPendingCommand: ArenaPendingCommand | null = null;
  #authenticationPromise: Promise<void> | null = null;
  #arenaRecoveryPromise: Promise<boolean> | null = null;
  #arenaAuthFailures = 0;
  #arenaAuthRetryAt = 0;
  #lastArenaSnapshotAt = 0;

  constructor(options: HttpGameServiceOptions = {}) {
    this.#origin = options.apiOrigin ?? window.location.origin;
    this.#fetch = options.fetchImpl ?? window.fetch.bind(window);
    const telegram = options.telegram ?? (window as TelegramWindow).Telegram?.WebApp ?? null;
    this.#telegram = telegram;
    this.#arenaRealtime = options.arenaRealtime ?? new WebSocketArenaTransport(websocketEndpoint(this.#origin));
    this.#now = options.now ?? Date.now;
    this.#wait = options.wait ?? defaultWait;
    this.#tapOutboxRepository = options.tapOutbox ?? new IndexedDbTapOutboxRepository();
    this.#tapOutboxOwnerKey = telegramOwnerKey(telegram);
    this.#onRequestMetric = options.onRequestMetric;
  }

  async load(): Promise<LoadResult> {
    await this.#authenticate();
    await this.#exclusiveSession.acquire(this.#tapOutboxOwnerKey);
    void this.#ensureTelegramWriteAccess();
    const response = await this.#requestJson<ApiEnvelope>(`${API_PREFIX}/bootstrap`, { method: "POST", body: "{}" });
    const loaded = this.#consumeEnvelope(response);
    await this.#initializeTapOutbox(loaded.state.season.id);
    const load = { ...loaded, pendingTaps: this.#pendingTapSummary() };
    if (load.state.season.status === "active") {
      await Promise.allSettled([this.#loadInviteUrl(), this.#loadPremiumOffers()]);
      this.#connectArena();
    } else {
      await Promise.allSettled([this.#loadInviteUrl()]);
    }
    return { ...load, state: this.#assertLoaded() };
  }

  async loadLeaderboard(): Promise<SeasonLeaderboard> {
    const response = await this.#requestJson<SeasonLeaderboard>(`${API_PREFIX}/leaderboard`, { method: "GET" });
    if (!Array.isArray(response.leaders) || response.leaders.length > 10 || !Number.isFinite(Date.parse(response.calculatedAt)) ||
      [...response.leaders, ...(response.self ? [response.self] : [])].some((row) =>
        !/^\d+$/.test(row.carats) || (row.rank !== null && (!Number.isSafeInteger(row.rank) || row.rank < 1)))) {
      throw new GameServiceError("INVALID_ACTION", "Invalid leaderboard response");
    }
    return response;
  }

  async sendDiagnostics(events: readonly GameAnalyticsEvent[]): Promise<void> {
    if (!this.#accessToken || this.#state === null) throw new GameServiceError("NETWORK", "Diagnostics wait for bootstrap");
    await this.#requestJson(`${API_PREFIX}/diagnostics/events`, { method: "POST", body: JSON.stringify({ events }), keepalive: true });
  }

  async submitSupport(request: SupportRequest): Promise<SupportReceipt> {
    return this.#requestJson<SupportReceipt>(`${API_PREFIX}/support/reports`, { method: "POST", body: JSON.stringify({ ...request, diagnostics: this.#recentRequestErrors }) });
  }

  loadSupportInbox(before?: string): Promise<SupportInbox> {
    return this.#requestJson(`${API_PREFIX}/support/tickets${before ? `?before=${encodeURIComponent(before)}` : ""}`, { method: "GET" });
  }
  loadSupportThread(reference: string): Promise<SupportThread> {
    return this.#requestJson(`${API_PREFIX}/support/tickets/${encodeURIComponent(reference)}`, { method: "GET" });
  }
  async replySupport(reference: string, requestId: string, message: string): Promise<void> {
    await this.#requestJson(`${API_PREFIX}/support/tickets/${encodeURIComponent(reference)}/messages`, { method: "POST", body: JSON.stringify({ requestId, message }) });
  }
  async markSupportRead(reference: string): Promise<void> {
    await this.#requestJson(`${API_PREFIX}/support/tickets/${encodeURIComponent(reference)}/read`, { method: "POST", body: "{}" });
  }
  watchSupport(onChange: () => void): () => void {
    return watchMarketStream(async signal => {
      const get = () => this.#fetch(new URL(`${API_PREFIX}/support/stream`, this.#origin), {
        headers: { Authorization: `Bearer ${this.#accessToken}`, Accept: "text/event-stream" }, signal, cache: "no-store",
      });
      const response = await get();
      if (response.status !== 401 || signal.aborted || this.#destroyed) return response;
      await response.body?.cancel(); await this.#authenticate(); return get();
    }, onChange, () => undefined, "support");
  }

  async loadClubRoster(input: { line: 1 | 2 | null; query: string; page: number }): Promise<ClubRosterResult> {
    this.#assertLoaded();
    const params = new URLSearchParams({ page: String(Math.max(1, Math.trunc(input.page))) });
    if (input.line !== null) params.set("line", String(input.line));
    const query = input.query.trim().slice(0, 40);
    if (query !== "") params.set("q", query);
    const response = await this.#requestJson<ClubRosterResponse>(`${API_PREFIX}/club?${params.toString()}`, {
      method: "GET",
    });
    if (
      typeof response.serverNow !== "string" ||
      !Array.isArray(response.friends) ||
      !isRecord(response.clubRoster) ||
      !isRecord(response.referrals)
    ) {
      throw new GameServiceError("INVALID_ACTION", "Invalid club roster response");
    }
    return response as ClubRosterResult;
  }

  async syncProduction(): Promise<ServiceEnvelope> {
    this.#assertLoaded();
    if ((this.#tapOutboxState?.batches.length ?? 0) > 0) return this.#cachedEnvelope();
    if (this.#now() < this.#nextSyncAt) return this.#cachedEnvelope();
    return this.#forceSync();
  }

  async refreshState(): Promise<ServiceEnvelope> {
    this.#assertLoaded();
    if (this.#syncPromise !== null) {
      try {
        await this.#syncPromise;
      } catch {
        // The recovery request below must still get one fresh authoritative
        // snapshot even when a background sync has just failed.
      }
    }
    return this.#forceSync();
  }

  submitTaps(commandId: string, count: number): Promise<TapResult> {
    this.#assertLoaded();
    if (!Number.isSafeInteger(count) || count < 1 || count > GAME_CONFIG.economy.maxTapBatchSize) {
      throw new GameServiceError("INVALID_ACTION", "Invalid tap count");
    }
    if (this.#tapOutboxState === null || this.#tapOutboxScope === "") {
      throw new GameServiceError("INVALID_ACTION", "Tap outbox is not initialized");
    }
    if (this.#tapWaiters.has(commandId)) {
      throw new GameServiceError("INVALID_ACTION", "Tap command is already pending");
    }
    let resolveResult: (result: TapResult) => void = () => undefined;
    let rejectResult: (error: unknown) => void = () => undefined;
    const result = new Promise<TapResult>((resolve, reject) => {
      resolveResult = resolve;
      rejectResult = reject;
    });
    this.#tapWaiters.set(commandId, { resolve: resolveResult, reject: rejectResult });
    void this.#enqueueTapBatch(commandId, count)
      .then(() => this.resumePendingTaps())
      .catch((error: unknown) => {
        this.#tapWaiters.delete(commandId);
        rejectResult(error);
      });
    return result;
  }

  stageTaps(commandId: string, count: number): void {
    this.#tapDrafts.put(commandId, count);
  }

  async #submitTapBatchOnce(batch: TapOutboxBatch): Promise<TapResult> {
    const seasonId = this.#tapOutboxState?.seasonId;
    const body = {
      ...this.#commandBody(batch.commandId, batch.clientSessionId),
      seasonId,
      firstSeq: batch.firstSeq,
      lastSeq: batch.lastSeq,
      requestedCount: batch.requestedCount,
      responseMode: "tap-delta-v1",
    };
    const response = await this.#requestEnvelope("/taps/batches", "POST", body, true);
    if (seasonId !== this.#tapOutboxState?.seasonId) throw new GameServiceError("SEASON_INACTIVE", "Season changed while sending taps");
    const receipt = isRecord(response.receipt) ? response.receipt : {};
    const envelope = response.responseMode === "tap-delta-v1"
      ? this.#consumeTapDelta(response, receipt)
      : this.#consumeEnvelope(response);
    const acceptedCount = integerValue(receipt.acceptedCount);
    const lastAcceptedSeq = typeof receipt.lastAcceptedSeq === "string"
      ? receipt.lastAcceptedSeq
      : (BigInt(batch.firstSeq) + BigInt(acceptedCount) - 1n).toString();
    const rejectionReason = receipt.rejectionReason === "RATE_LIMIT" || receipt.rejectionReason === "ENERGY_EMPTY"
      ? receipt.rejectionReason
      : null;
    return {
      ...envelope,
      requestedCount: integerValue(receipt.requestedCount, batch.requestedCount),
      acceptedCount,
      rejectedCount: integerValue(receipt.rejectedCount, Math.max(0, batch.requestedCount - acceptedCount)),
      energySpent: integerValue(receipt.energySpent),
      rewardPerTap: typeof receipt.rewardPerTap === "string" ? receipt.rewardPerTap : envelope.state.account.tapReward,
      lastAcceptedSeq,
      rejectionReason,
      duplicate: receipt.duplicate === true,
    };
  }

  async #initializeTapOutbox(seasonId: string): Promise<void> {
    const scope = `v1:${this.#tapOutboxOwnerKey}:${seasonId}`;
    const stored = await this.#tapOutboxRepository.load(scope);
    const loaded = stored?.ownerKey === this.#tapOutboxOwnerKey && stored.seasonId === seasonId ? stored : null;
    this.#tapOutboxScope = scope;
    this.#tapOutboxState = loaded ?? {
      version: 1,
      ownerKey: this.#tapOutboxOwnerKey,
      seasonId,
      clientSessionId: newSessionId("tap-session"),
      lastAssignedSeq: "0",
      batches: [],
    };
    this.#tapDrafts.open(scope);
    for (const [id, count] of this.#tapDrafts.entries()) {
      if (!this.#tapOutboxState.batches.some((batch) => batch.rootCommandId === id)) await this.#enqueueTapBatch(id, count);
      this.#tapDrafts.remove(id);
    }
  }

  async #enqueueTapBatch(rootCommandId: string, count: number): Promise<void> {
    const tapReward = tapBoostAt(this.#assertLoaded(), Date.parse(this.#cachedEnvelope().serverNow)).reward;
    await this.#mutateTapOutbox((current) => {
      const firstSeq = BigInt(current.lastAssignedSeq) + 1n;
      const lastSeq = firstSeq + BigInt(count) - 1n;
      const batch: TapOutboxBatch = {
        rootCommandId,
        commandId: rootCommandId,
        clientSessionId: current.clientSessionId,
        firstSeq: firstSeq.toString(),
        lastSeq: lastSeq.toString(),
        requestedCount: count,
        originalRequestedCount: count,
        acceptedCount: 0,
        energySpent: 0,
        optimisticRewardPerTap: tapReward,
        rateRetryAttempt: 0,
        deliveryAttempts: 0,
        nextAttemptAt: this.#now(),
        createdAt: this.#now(),
      };
      return {
        ...current,
        lastAssignedSeq: lastSeq.toString(),
        batches: [...current.batches, batch],
      };
    });
    // Remove only after outbox persistence, and before any network delivery.
    this.#tapDrafts.remove(rootCommandId);
  }

  resumePendingTaps(): void {
    if (
      this.#destroyed ||
      this.#tapOutboxState === null ||
      this.#tapOutboxState.batches.length === 0 ||
      this.#tapDrainPromise !== null
    ) {
      return;
    }
    this.#tapDrainPromise = this.#drainTapOutbox()
      .catch(async (error: unknown) => {
        console.error("Diamond Tapper tap outbox failed", error);
        // A storage failure sits outside the normal HTTP retry branch. Keep a
        // bounded retry cadence instead of recursively spinning the main
        // thread while the browser storage layer is temporarily unavailable.
        await this.#wait(TAP_RETRY_BASE_MS);
      })
      .finally(() => {
        this.#tapDrainPromise = null;
        if (!this.#destroyed && (this.#tapOutboxState?.batches.length ?? 0) > 0) {
          this.resumePendingTaps();
        }
      });
  }

  async #drainTapOutbox(): Promise<void> {
    while (!this.#destroyed) {
      const batch = this.#tapOutboxState?.batches[0];
      if (batch === undefined) return;
      // Persist wall-clock deadlines across reloads, but never let a device
      // clock correction postpone a queued batch for longer than our retry cap.
      const waitFor = Math.min(TAP_RETRY_MAX_MS, Math.max(0, batch.nextAttemptAt - this.#now()));
      if (waitFor > 0) await this.#wait(waitFor);
      if (this.#destroyed) return;

      let result: TapResult;
      try {
        result = await this.#submitTapBatchOnce(batch);
      } catch (error) {
        const expectedFirstSeq = error instanceof GameServiceError ? error.details.expectedFirstSeq : undefined;
        // The server checks an existing command receipt BEFORE the sequence.
        // Thus this specific rejection proves this exact command did not post.
        // Recover old persisted gaps without changing IDs or replaying a
        // previously accepted prefix. Unknown/network outcomes never rebase.
        if (error instanceof GameServiceError && error.message === "SEQUENCE_CONFLICT"
          && typeof expectedFirstSeq === "string" && /^[1-9]\d{0,18}$/.test(expectedFirstSeq)
          && (batch.sequenceRecoveryAttempts ?? 0) < 3) {
          await this.#mutateTapOutbox((current) => this.#rebaseTapQueue(current,
            batch.clientSessionId, BigInt(expectedFirstSeq), batch.rootCommandId));
          continue;
        }
        const retryable = !(error instanceof GameServiceError) || error.code === "NETWORK";
        if (retryable) {
          const updated = await this.#replaceTapBatch(batch.rootCommandId, (current) => {
            const deliveryAttempts = current.deliveryAttempts + 1;
            const retry = { ...current, deliveryAttempts };
            return { ...retry, nextAttemptAt: this.#now() + deterministicTapRetryDelay(retry) };
          });
          this.#online = false;
          this.#notify(this.#cachedEnvelope());
          if (updated === null) return;
          continue;
        }
        await this.#removeTapBatch(batch.rootCommandId);
        const waiter = this.#tapWaiters.get(batch.rootCommandId);
        this.#tapWaiters.delete(batch.rootCommandId);
        waiter?.reject(error);
        if (error instanceof GameServiceError && error.code === "SEASON_INACTIVE") {
          // This is a season boundary, not a broken connection. Read the final
          // confirmed balance even while the remaining local queue is draining.
          try { await this.#forceSync(); } catch { /* Normal reconnect retries the read. */ }
        }
        this.#notify(this.#cachedEnvelope());
        continue;
      }

      this.#online = true;
      const acceptedCount = batch.acceptedCount + result.acceptedCount;
      const energySpent = batch.energySpent + result.energySpent;
      const remaining = Math.max(0, batch.originalRequestedCount - acceptedCount);
      if (
        remaining > 0 &&
        result.rejectionReason === "RATE_LIMIT" &&
        batch.rateRetryAttempt < GAME_CONFIG.mock.tapRateLimitRetryMaxAttempts
      ) {
        const retryAttempt = batch.rateRetryAttempt + 1;
        const retryFromSeq = (BigInt(result.lastAcceptedSeq) + 1n).toString();
        await this.#replaceTapBatch(batch.rootCommandId, (current) => ({
          ...current,
          commandId: `${current.rootCommandId}:rate-retry:${retryAttempt}`,
          firstSeq: retryFromSeq,
          requestedCount: remaining,
          acceptedCount,
          energySpent,
          rateRetryAttempt: retryAttempt,
          deliveryAttempts: 0,
          nextAttemptAt: this.#now(),
        }));
        this.#notify(this.#cachedEnvelope());
        const refillDelay = Math.ceil(
          (remaining * 1_000) / Math.max(1, GAME_CONFIG.economy.maxAcceptedTapsPerSecond),
        );
        await this.#wait(Math.max(GAME_CONFIG.mock.tapRateLimitRetryFloorMs, refillDelay));
        continue;
      }

      // Discarded ENERGY_EMPTY taps (or an exhausted rate retry) do not consume
      // server sequence numbers. Rebase only the still-unsent following queue
      // in the SAME atomic persistence step as removing the finished head.
      await this.#mutateTapOutbox((current) => this.#rebaseTapQueue({
        ...current, batches: current.batches.filter((item) => item.rootCommandId !== batch.rootCommandId),
      }, batch.clientSessionId, BigInt(result.lastAcceptedSeq) + 1n));
      const completed: TapResult = {
        ...result,
        requestedCount: batch.originalRequestedCount,
        acceptedCount,
        rejectedCount: remaining,
        energySpent,
        pendingTaps: this.#pendingTapSummary(),
      };
      const waiter = this.#tapWaiters.get(batch.rootCommandId);
      this.#tapWaiters.delete(batch.rootCommandId);
      if (waiter === undefined) this.#notify(completed);
      else waiter.resolve(completed);
    }
  }

  async #replaceTapBatch(
    rootCommandId: string,
    update: (batch: TapOutboxBatch) => TapOutboxBatch,
  ): Promise<TapOutboxBatch | null> {
    let replacement: TapOutboxBatch | null = null;
    await this.#mutateTapOutbox((current) => ({
      ...current,
      batches: current.batches.map((batch) => {
        if (batch.rootCommandId !== rootCommandId) return batch;
        replacement = update(batch);
        return replacement;
      }),
    }));
    return replacement;
  }

  #rebaseTapQueue(current: TapOutboxState, sessionId: string, nextSeq: bigint, recoveringRoot?: string): TapOutboxState {
    let cursor = nextSeq;
    const batches = current.batches.map((item) => {
      if (item.clientSessionId !== sessionId) return item;
      const firstSeq = cursor;
      cursor += BigInt(item.requestedCount);
      return { ...item, firstSeq: firstSeq.toString(), lastSeq: (cursor - 1n).toString(),
        ...(item.rootCommandId === recoveringRoot
          ? { sequenceRecoveryAttempts: (item.sequenceRecoveryAttempts ?? 0) + 1, nextAttemptAt: this.#now() } : {}),
      };
    });
    return { ...current, batches,
      lastAssignedSeq: current.clientSessionId === sessionId ? (cursor - 1n).toString() : current.lastAssignedSeq };
  }

  async #removeTapBatch(rootCommandId: string): Promise<void> {
    await this.#mutateTapOutbox((current) => ({
      ...current,
      batches: current.batches.filter((batch) => batch.rootCommandId !== rootCommandId),
    }));
  }

  async #mutateTapOutbox(update: (state: TapOutboxState) => TapOutboxState): Promise<TapOutboxState> {
    let resolveMutation: (state: TapOutboxState) => void = () => undefined;
    let rejectMutation: (error: unknown) => void = () => undefined;
    const mutation = new Promise<TapOutboxState>((resolve, reject) => {
      resolveMutation = resolve;
      rejectMutation = reject;
    });
    this.#tapOutboxWriteTail = this.#tapOutboxWriteTail
      .then(async () => {
        if (this.#tapOutboxState === null || this.#tapOutboxScope === "") {
          throw new GameServiceError("INVALID_ACTION", "Tap outbox is not initialized");
        }
        const next = update(structuredClone(this.#tapOutboxState));
        await this.#tapOutboxRepository.save(this.#tapOutboxScope, next);
        this.#tapOutboxState = next;
        resolveMutation(next);
      })
      .catch((error: unknown) => {
        rejectMutation(error);
      });
    return mutation;
  }

  #pendingTapSummary() {
    return pendingTapSummary(this.#tapOutboxState, this.#now());
  }

  async purchaseUpgrade(commandId: string, upgradeCode: UpgradeCode): Promise<UpgradeResult> {
    const response = await this.#requestEnvelope(
      `/upgrades/${encodeURIComponent(upgradeCode)}/purchase`,
      "POST",
      this.#commandBody(commandId),
    );
    const envelope = this.#consumeEnvelope(response);
    const receipt = isRecord(response.receipt) ? response.receipt : {};
    return {
      ...envelope,
      purchased: receipt.purchased !== false,
      duplicate: receipt.duplicate === true,
      upgradeCode,
      level: integerValue(receipt.upgradeLevel, this.#upgradeLevel(envelope.state, upgradeCode)),
    };
  }

  purchaseMechanicalCutter(commandId: string): Promise<UpgradeResult> {
    return this.purchaseUpgrade(commandId, "mechanicalCutter");
  }

  async refillEnergy(commandId: string): Promise<EnergyRefillResult> {
    const response = await this.#requestEnvelope("/energy/refills", "POST", this.#commandBody(commandId));
    const envelope = this.#consumeEnvelope(response);
    const receipt = isRecord(response.receipt) ? response.receipt : {};
    return {
      ...envelope,
      restored: integerValue(receipt.energyRestored),
      cost: typeof receipt.refillCost === "string" ? receipt.refillCost : "0",
      duplicate: receipt.duplicate === true,
    };
  }

  async claimDailyReward(commandId: string): Promise<ServiceEnvelope> {
    const state = this.#assertLoaded();
    const day = state.progress.daily.utcDay;
    return this.#claimEnvelope(`/daily/${encodeURIComponent(day)}/claim`, commandId,
      next => next.progress.daily.utcDay === day && next.progress.daily.rewardClaimed);
  }

  async claimAchievement(commandId: string, achievementId: AchievementId = "living-facet"): Promise<ServiceEnvelope> {
    return this.#claimEnvelope(`/achievements/${encodeURIComponent(achievementId)}/claim`, commandId,
      next => next.progress.claimedAchievementIds.includes(achievementId));
  }

  async claimSeasonTask(commandId: string, taskCode: SeasonTaskCode): Promise<ServiceEnvelope> {
    if (taskCode === "season-channel" || taskCode === "season-chat") {
      const membership = await this.#requestEnvelope("/membership/check", "POST", this.#commandBody(commandId));
      this.#consumeEnvelope(membership);
    }
    return this.#claimEnvelope(`/season-tasks/${encodeURIComponent(taskCode)}/claim`, commandId,
      next => next.progress.seasonal.claimedCodes.includes(taskCode));
  }

  async refreshCommunityMembership(_target?: "channel" | "chat"): Promise<ServiceEnvelope> {
    const membership = await this.#requestEnvelope(
      "/membership/check",
      "POST",
      this.#commandBody(newSessionId("membership-check")),
    );
    return this.#consumeEnvelope(membership);
  }

  async purchasePremiumOffer(commandId: string, offerId: PremiumOfferId): Promise<ServiceEnvelope> {
    let versionId = this.#offerVersions.get(offerId);
    if (versionId === undefined) {
      await this.#loadPremiumOffers();
      versionId = this.#offerVersions.get(offerId);
    }
    if (versionId === undefined) throw new GameServiceError("INVALID_ACTION", "Premium offer is unavailable");
    const invoice = await this.#requestJson<InvoiceResponse>(
      `${API_PREFIX}/premium/offers/${encodeURIComponent(versionId)}/invoice`,
      { method: "POST", body: JSON.stringify(this.#commandBody(commandId)) },
    );
    if (invoice.status !== "PAID") await this.#payInvoice(invoice);
    await this.#loadPremiumOffers();
    return this.#forceSync();
  }

  async refreshPremiumOffers(): Promise<void> {
    await this.#loadPremiumOffers();
    this.#notify(this.#cachedEnvelope());
  }

  async selectDiamondSkin(skinId: DiamondSkinId | null): Promise<ServiceEnvelope> {
    const response = await this.#requestEnvelope("/profile/skin", "PUT", { skinId });
    return this.#consumeEnvelope(response);
  }

  async selectProfileTitle(achievementId: AchievementId): Promise<ServiceEnvelope> {
    const response = await this.#requestEnvelope("/profile/title", "PUT", { achievementId });
    return this.#consumeEnvelope(response);
  }

  async advanceOnboarding(commandId: string, action: OnboardingAction): Promise<ServiceEnvelope> {
    const response = await this.#requestEnvelope("/onboarding/actions", "POST", {
      ...this.#commandBody(commandId),
      action,
    });
    return this.#consumeEnvelope(response);
  }

  async createPvpMatch(commandId: string, stake: string, gameId: ArenaGameId = "crystal-penalty"): Promise<ServiceEnvelope> {
    const state = this.#assertLoaded();
    if (state.pvp.status !== "idle") return this.#cachedEnvelope();
    return this.#sendArenaCommand(
      { type: "arena.queue", commandId, gameId, stake },
      (match) => match.status !== "idle",
    );
  }

  async submitPvpMove(commandId: string, zone: number): Promise<ServiceEnvelope> {
    const state = this.#assertLoaded();
    const matchId = state.pvp.matchId;
    const turnIndex = state.pvp.currentTurn?.index;
    if (
      matchId === null ||
      turnIndex === undefined ||
      state.pvp.status !== "choosing" ||
      state.pvp.currentTurn?.playerZone !== null
    ) return this.#cachedEnvelope();
    return this.#sendArenaCommand(
      { type: "arena.move", commandId, matchId, turnIndex, zone },
      (match) =>
        match.matchId !== matchId ||
        match.currentTurn?.index !== turnIndex ||
        match.currentTurn.playerZone !== null ||
        match.status === "resolved" ||
        match.status === "result",
    );
  }

  canRequestGuardianMatch(): boolean {
    return false;
  }

  requestGuardianMatch(_commandId: string): Promise<ServiceEnvelope> {
    return Promise.reject(new GameServiceError("INVALID_ACTION", "Manual Guardian matching is not supported"));
  }

  async cancelPvpMatch(commandId: string): Promise<ServiceEnvelope> {
    const state = this.#assertLoaded();
    if (state.pvp.status !== "matchmaking") return this.#cachedEnvelope();
    return this.#sendArenaCommand(
      { type: "arena.cancel", commandId },
      (match) => match.status === "idle" || (match.status === "result" && match.result === "cancelled"),
    );
  }

  async leavePvpResult(commandId: string): Promise<ServiceEnvelope> {
    const state = this.#assertLoaded();
    if (state.pvp.status === "idle") return this.#cachedEnvelope();
    if (state.pvp.status !== "result") {
      throw new GameServiceError("ARENA_SYNC", "Arena result is no longer active");
    }
    const previousMatchId = state.pvp.matchId;
    const previousSequence = state.pvp.sequence;
    const previousEventSequence = state.pvp.eventSequence;
    return this.#sendArenaCommand(
      { type: "arena.dismiss", commandId },
      (match) =>
        match.status === "idle" ||
        (match.status === "result" &&
          (match.matchId !== previousMatchId ||
            match.sequence !== previousSequence ||
            match.eventSequence !== previousEventSequence)),
    );
  }

  async claimSeasonReward(commandId: string, allocationId?: string): Promise<ServiceEnvelope> {
    const state = this.#assertLoaded();
    const targetAllocationId = allocationId ?? state.season.allocationId;
    if (targetAllocationId === null || targetAllocationId === undefined) {
      throw new GameServiceError("INVALID_ACTION", "Season allocation is not available");
    }
    const invoice = await this.#requestJson<InvoiceResponse>(
      `${API_PREFIX}/allocations/${encodeURIComponent(targetAllocationId)}/claim/invoice`,
      { method: "POST", body: JSON.stringify(this.#commandBody(commandId)) },
    );
    if (invoice.status !== "CLAIMED") await this.#payInvoice(invoice);
    return this.#forceSync();
  }

  async updateSettings(settings: Partial<UserSettings>): Promise<ServiceEnvelope> {
    const response = await this.#requestEnvelope("/profile/settings", "PUT", settings);
    return this.#consumeEnvelope(response);
  }

  seedScenario(_scenario: DevScenarioId): Promise<LoadResult> {
    return Promise.reject(new GameServiceError("INVALID_ACTION", "Dev scenarios are unavailable on the live adapter"));
  }

  setOnline(online: boolean): void {
    this.#online = online;
    if (online) this.resumePendingTaps();
  }

  getOnline(): boolean {
    return this.#online;
  }

  getPendingTaps() {
    return this.#pendingTapSummary();
  }

  getInviteUrl(): string {
    return this.#inviteUrl;
  }

  subscribe(listener: (envelope: ServiceEnvelope) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  destroy(): void {
    this.#destroyed = true;
    // A pending storage write can finish after a page close. Keep ownership
    // until that write is done, so a newly opened page never reads half a queue.
    void this.#tapOutboxWriteTail.finally(() => this.#exclusiveSession.release());
    const pending = this.#takeArenaPendingCommand();
    pending?.reject(new GameServiceError("NETWORK", "Arena connection closed"));
    this.#arenaConnection?.close();
    this.#arenaConnection = null;
    this.#tapWaiters.clear();
    this.#listeners.clear();
  }

  async #authenticate(): Promise<void> {
    if (this.#authenticationPromise !== null) return this.#authenticationPromise;
    this.#authenticationPromise = this.#performAuthentication();
    try {
      await this.#authenticationPromise;
    } finally {
      this.#authenticationPromise = null;
    }
  }

  async #performAuthentication(): Promise<void> {
    if (this.#telegram === null || this.#telegram.initData.trim() === "") {
      throw new GameServiceError("INVALID_ACTION", "Telegram Mini App authorization is required");
    }
    const rawStartParam = this.#telegram.initDataUnsafe?.start_param;
    const startParam = typeof rawStartParam === "string" && rawStartParam.trim() !== "" ? rawStartParam : undefined;
    const response = await this.#fetchJson<{ token?: unknown }>("/api/auth/telegram", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ initData: this.#telegram.initData, ...(startParam !== undefined ? { startParam } : {}) }),
    }, false);
    this.#accessToken = stringValue(response.token, "Telegram access token");
  }

  async #ensureTelegramWriteAccess(): Promise<void> {
    try {
      const current = await this.#requestJson<TelegramWriteAccessResponse>(
        `${API_PREFIX}/telegram/write-access`,
        { method: "GET" },
      );
      if (current.status === "allowed" || current.status === "cancelled") return;
      const result = await requestTelegramWriteAccess(this.#telegram);
      if (result !== "allowed" && result !== "cancelled") return;
      const status: TelegramWriteAccessChoice = result;
      await this.#requestJson<TelegramWriteAccessResponse>(`${API_PREFIX}/telegram/write-access`, {
        method: "POST",
        body: JSON.stringify({ status }),
      });
    } catch {
      // Notification consent must never prevent the game from loading. A failed
      // persistence attempt is retried on the next launch; Telegram returns an
      // already granted permission without showing the prompt again.
    }
  }

  async #forceSync(): Promise<ServiceEnvelope> {
    if (this.#syncPromise !== null) return this.#syncPromise;
    const previousSeasonStatus = this.#state?.season.status ?? null;
    const previousSeasonId = this.#state?.season.id;
    this.#syncPromise = this.#requestEnvelope("/sync", "POST", {}).then(async (response) => {
      const envelope = this.#consumeEnvelope(response);
      if (this.#tapOutboxState?.seasonId !== envelope.state.season.id) {
        for (const waiter of this.#tapWaiters.values()) waiter.reject(new GameServiceError("SEASON_INACTIVE", "Season changed"));
        this.#tapWaiters.clear();
        await this.#tapOutboxWriteTail;
        await this.#initializeTapOutbox(envelope.state.season.id);
      }
      if ((previousSeasonStatus !== "active" || previousSeasonId !== envelope.state.season.id) && envelope.state.season.status === "active") {
        await Promise.allSettled([this.#loadPremiumOffers()]);
        this.#connectArena();
      }
      return { ...envelope, pendingTaps: this.#pendingTapSummary() };
    });
    try {
      return await this.#syncPromise;
    } finally {
      this.#syncPromise = null;
    }
  }

  #claimEnvelope(path: string, commandId: string, isClaimed: (state: GameState) => boolean): Promise<ServiceEnvelope> {
    const seasonId = this.#assertLoaded().season.id;
    const key = `${seasonId}:${path}`;
    const pending = this.#rewardClaims.get(key);
    if (pending) return pending;
    const confirmed = (state: GameState) => state.season.id === seasonId && isClaimed(state);
    const run = async (): Promise<ServiceEnvelope> => {
      let alreadyClaimed: boolean;
      let envelope: ServiceEnvelope;
      try {
        const response = await this.#requestEnvelope(path, "POST", this.#commandBody(commandId));
        envelope = this.#consumeEnvelope(response);
        alreadyClaimed = isRecord(response.receipt) && response.receipt.duplicate === true;
      } catch (error) {
        // A duplicate is not proof of a reward. Read a NEW snapshot (not the
        // cached/in-flight pre-claim sync) and verify the same season and target.
        if (!(error instanceof GameServiceError) || error.message !== "IDEMPOTENCY_CONFLICT" ||
          !["claimed_by_another_command", "already_collected"].includes(error.details.reason ?? "")) throw error;
        envelope = await this.refreshState();
        if (!confirmed(envelope.state)) throw error;
        alreadyClaimed = true;
      }
      // A newer tap delta can arrive before the full claim response. Keep its
      // balance, but fetch the reward projection before presenting success.
      if (!confirmed(envelope.state)) envelope = await this.refreshState();
      if (!confirmed(envelope.state)) throw new GameServiceError("NETWORK", "Reward confirmation pending");
      return { ...envelope, rewardClaim: { alreadyClaimed } };
    };
    const promise = run().finally(() => this.#rewardClaims.delete(key));
    this.#rewardClaims.set(key, promise);
    return promise;
  }

  #commandBody(commandId: string, clientSessionId = this.#commandSessionId): Record<string, unknown> {
    return {
      commandId,
      expectedRevision: this.#state === null ? null : String(this.#state.revision),
      clientSessionId,
    };
  }

  async #requestEnvelope(
    path: string,
    method: "POST" | "PUT",
    body: unknown,
    keepalive = false,
  ): Promise<ApiEnvelope> {
    return this.#requestJson<ApiEnvelope>(`${API_PREFIX}${path}`, {
      method,
      body: JSON.stringify(body),
      keepalive,
    });
  }

  async #requestJson<T>(path: string, init: RequestInit): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    headers.set("Authorization", `Bearer ${this.#accessToken}`);
    const response = await this.#fetchJson<T>(path, { ...init, headers }, true);
    if (response === undefined) throw new GameServiceError("NETWORK", "Empty response");
    return response;
  }

  async #fetchJson<T>(path: string, init: RequestInit, allowReauth: boolean): Promise<T> {
    // A hung fetch (including a stalled response body) must not hold the whole
    // durable queue forever. Retry the SAME command; a timeout is not a receipt.
    if (path === `${API_PREFIX}/taps/batches` || path === `${API_PREFIX}/sync`) {
      const controller = new AbortController();
      let timer: ReturnType<typeof setTimeout> | undefined;
      const deadline = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          this.#online = false;
          this.#onRequestMetric?.({ route: analyticsRoute(path, this.#origin), method: init.method ?? "GET",
            durationMs: TAP_REQUEST_TIMEOUT_MS, status: 0, ok: false, apiCode: "DELIVERY_TIMEOUT" });
          reject(new GameServiceError("NETWORK", "Request confirmation timed out"));
        }, TAP_REQUEST_TIMEOUT_MS);
      });
      try {
        return await Promise.race([this.#fetchJsonAttempt<T>(path, { ...init, signal: controller.signal }, allowReauth), deadline]);
      } finally { clearTimeout(timer); }
    }
    return this.#fetchJsonAttempt<T>(path, init, allowReauth);
  }

  async #fetchJsonAttempt<T>(path: string, init: RequestInit, allowReauth: boolean): Promise<T> {
    let response: Response;
    const startedAt = this.#now();
    try {
      response = await this.#fetch(new URL(path, this.#origin), init);
    } catch (error) {
      this.#onRequestMetric?.({
        route: analyticsRoute(path, this.#origin),
        method: init.method ?? "GET",
        durationMs: Math.max(0, this.#now() - startedAt),
        status: 0,
        ok: false,
      });
      this.#online = false;
      throw new GameServiceError("NETWORK", error instanceof Error ? error.message : "Network request failed");
    }
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    // Some WebViews finish an aborted request late. It may have been accepted
    // server-side, but only the active retry can consume its idempotent receipt.
    if (init.signal?.aborted) throw new GameServiceError("NETWORK", "Request confirmation timed out");
    const apiError = isRecord(payload) && isRecord(payload.error) ? payload.error : null;
    const apiCode = typeof apiError?.code === "string" && /^[A-Za-z_][A-Za-z0-9_]{0,127}$/.test(apiError.code) ? apiError.code : undefined;
    const requestId = isRecord(payload) && typeof payload.requestId === "string" && /^[\w-]{1,100}$/.test(payload.requestId) ? payload.requestId : undefined;
    const errorDetails = isRecord(apiError?.details) ? apiError.details : null;
    const reason = typeof errorDetails?.reason === "string" && /^[a-z_]{1,80}$/.test(errorDetails.reason) ? errorDetails.reason : undefined;
    this.#onRequestMetric?.({ route: analyticsRoute(path, this.#origin), method: init.method ?? "GET",
      durationMs: Math.max(0, this.#now() - startedAt), status: response.status, ok: response.ok,
      ...(apiCode ? { apiCode } : {}), ...(reason ? { reason } : {}), ...(requestId ? { requestId } : {}),
    });
    if (!response.ok && !path.includes("/diagnostics/") && !path.includes("/support/")) {
      this.#recentRequestErrors.push({ eventId: newSessionId("request-error"), name: "request_complete",
        occurredAt: new Date().toISOString(), context: { clientVersion: "http", platform: "unknown", locale: "unknown" },
        properties: { route: analyticsRoute(path, this.#origin), method: init.method ?? "GET", status: response.status, ok: false,
          ...(apiCode ? { apiCode } : {}), ...(requestId ? { requestId } : {}) } });
      this.#recentRequestErrors = this.#recentRequestErrors.slice(-20);
    }
    if (response.status === 401 && allowReauth) {
      await this.#authenticate();
      const headers = new Headers(init.headers);
      headers.set("Authorization", `Bearer ${this.#accessToken}`);
      if (init.signal?.aborted) throw new GameServiceError("NETWORK", "Request confirmation timed out");
      return this.#fetchJsonAttempt<T>(path, { ...init, headers }, false);
    }
    if (!response.ok) {
      const error = serviceError(isRecord(payload) ? payload : {}, response.status);
      if (error.code === "NETWORK") this.#online = false;
      throw error;
    }
    this.#online = true;
    return payload as T;
  }

  #consumeEnvelope(response: ApiEnvelope): LoadResult {
    let state = parseState(response.state);
    const incomingServerNow = stringValue(response.serverNow, "server time");
    const incomingAt = Date.parse(incomingServerNow);
    const previous = this.#state;
    const staleWithinSeason =
      previous !== null &&
      previous.season.id === state.season.id &&
      (state.revision < previous.revision ||
        (state.revision === previous.revision && incomingAt < Date.parse(this.#serverNow)));
    if (staleWithinSeason && previous !== null) {
      // Tap deltas advance the monetary revision without carrying claim flags.
      // A slower full response can still prove a claim for the SAME day/season.
      // Merge only monotonic receipts, never old balances or a different day.
      const dailyClaimed = previous.progress.daily.utcDay === state.progress.daily.utcDay
        && state.progress.daily.rewardClaimed;
      state = {
        ...previous,
        onboarding: reconcileOnboardingProgress(previous.onboarding, state.onboarding),
        progress: {
          ...previous.progress,
          daily: { ...previous.progress.daily, rewardClaimed: previous.progress.daily.rewardClaimed || dailyClaimed,
            ...(dailyClaimed && !previous.progress.daily.rewardClaimed ? {
              rewardClaimedAt: state.progress.daily.rewardClaimedAt,
              claimedReward: state.progress.daily.claimedReward,
            } : {}) },
          dailyStreak: dailyClaimed ? {
            current: Math.max(previous.progress.dailyStreak.current, state.progress.dailyStreak.current),
            longest: Math.max(previous.progress.dailyStreak.longest, state.progress.dailyStreak.longest),
            lastClaimedUtcDay: previous.progress.daily.utcDay,
          } : previous.progress.dailyStreak,
          claimedAchievementIds: [...new Set([...previous.progress.claimedAchievementIds, ...state.progress.claimedAchievementIds])],
          seasonal: { ...previous.progress.seasonal,
            claimedCodes: [...new Set([...previous.progress.seasonal.claimedCodes, ...state.progress.seasonal.claimedCodes])] },
        },
      };
    }
    if (!staleWithinSeason && previous?.season.id === state.season.id && (
      incomingAt < this.#lastArenaSnapshotAt ||
      (state.pvp.matchId === previous.pvp.matchId && state.pvp.eventSequence < previous.pvp.eventSequence)
    )) {
      state = { ...state, pvp: previous.pvp };
    }
    if (previous?.season.id !== state.season.id) {
      this.#lastArenaSnapshotAt = 0;
      this.#premiumOfferStates = null; this.#offerVersions.clear(); this.#premiumCatalogSequence++;
    }
    state = { ...state, premium: { ...state.premium,
      offerStates: this.#premiumOfferStates?.seasonId === state.season.id ? this.#premiumOfferStates.offers : {},
    } };
    if (!staleWithinSeason && response.config !== undefined) applyPublishedGameConfig(response.config, state);
    this.#state = state;
    this.#serverNow =
      Date.parse(this.#serverNow) > Date.parse(incomingServerNow)
        ? this.#serverNow
        : incomingServerNow;
    const receivedAt = this.#now();
    this.#serverAnchor = receivedAt;
    const recommended = typeof response.nextRecommendedSyncAt === "string"
      ? Date.parse(response.nextRecommendedSyncAt)
      : Number.NaN;
    const serverNow = Date.parse(this.#serverNow);
    this.#nextSyncAt = Number.isFinite(recommended) && Number.isFinite(serverNow)
      ? receivedAt + Math.max(0, recommended - serverNow)
      : receivedAt + 30_000;
    const envelope = { state, serverNow: this.#serverNow, pendingTaps: this.#pendingTapSummary() };
    return {
      ...envelope,
      offlineGain: typeof response.offlineGain === "string" ? response.offlineGain : "0",
      offlineSeconds: integerValue(response.offlineSeconds),
      offlineReserveUsedSeconds: integerValue(response.offlineReserveUsedSeconds),
    };
  }

  #consumeTapDelta(response: ApiEnvelope, receipt: Record<string, unknown>): ServiceEnvelope {
    const current = this.#assertLoaded();
    if (!isRecord(response.tapState)) throw new Error("Incomplete Diamond Tapper tap delta");
    const raw = response.tapState;
    const delta: ApiTapDeltaState = {
      stateRevision: stringValue(raw.stateRevision, "tap state revision"),
      available: stringValue(raw.available, "tap available balance"),
      reserved: stringValue(raw.reserved, "tap reserved balance"),
      energyCurrent: integerValue(raw.energyCurrent, -1),
      energyRemainderMs: stringValue(raw.energyRemainderMs, "tap energy remainder"),
      lastEnergyAt: stringValue(raw.lastEnergyAt, "tap energy time"),
      confirmedTaps: integerValue(raw.confirmedTaps, -1),
      primaryProduced: stringValue(raw.primaryProduced, "tap primary production"),
      ...(raw.firstTapHintSeen === true ? { firstTapHintSeen: true as const } : {}),
    };
    const revision = Number(delta.stateRevision);
    if (!Number.isSafeInteger(revision) || delta.energyCurrent < 0 || delta.confirmedTaps < 0) {
      throw new Error("Invalid Diamond Tapper tap delta");
    }
    const duplicate = receipt.duplicate === true;
    const acceptedCount = integerValue(receipt.acceptedCount);
    const energySpent = integerValue(receipt.energySpent);
    const rewardTotal = typeof receipt.rewardTotal === "string"
      ? receipt.rewardTotal
      : (BigInt(acceptedCount) * BigInt(current.account.tapReward)).toString();
    const state = revision < current.revision
      ? current
      : {
          ...current,
          revision,
          account: {
            ...current.account,
            available: delta.available,
            reserved: delta.reserved,
          },
          energy: { ...current.energy, current: delta.energyCurrent },
          progress: {
            ...current.progress,
            confirmedTaps: delta.confirmedTaps,
            primaryProduced: delta.primaryProduced,
            daily: duplicate
              ? current.progress.daily
              : {
                  ...current.progress.daily,
                  confirmedTaps: current.progress.daily.confirmedTaps + acceptedCount,
                  primaryProduced: (BigInt(current.progress.daily.primaryProduced) + BigInt(rewardTotal)).toString(),
                  energySpent: current.progress.daily.energySpent + energySpent,
                },
          },
          meta: {
            ...current.meta,
            lastEnergyAt: delta.lastEnergyAt,
            energyRemainderMilliseconds: Number(delta.energyRemainderMs),
            firstTapHintSeen: delta.firstTapHintSeen === true || current.meta.firstTapHintSeen,
          },
        };
    this.#state = state;
    const incomingServerNow = stringValue(response.serverNow, "server time");
    if (Date.parse(incomingServerNow) >= Date.parse(this.#serverNow)) this.#serverNow = incomingServerNow;
    this.#serverAnchor = this.#now();
    const recommended = typeof response.nextRecommendedSyncAt === "string"
      ? Date.parse(response.nextRecommendedSyncAt)
      : Number.NaN;
    const serverNow = Date.parse(this.#serverNow);
    this.#nextSyncAt = Number.isFinite(recommended) && Number.isFinite(serverNow)
      ? this.#now() + Math.max(0, recommended - serverNow)
      : this.#now() + 30_000;
    return { state, serverNow: this.#serverNow, pendingTaps: this.#pendingTapSummary() };
  }

  #cachedEnvelope(): ServiceEnvelope {
    const state = this.#assertLoaded();
    const base = Date.parse(this.#serverNow);
    const serverNow = Number.isFinite(base)
      ? new Date(base + Math.max(0, this.#now() - this.#serverAnchor)).toISOString()
      : new Date(this.#now()).toISOString();
    return { state, serverNow, pendingTaps: this.#pendingTapSummary() };
  }

  #assertLoaded(): GameState {
    if (this.#state === null) throw new GameServiceError("INVALID_ACTION", "Game state is not loaded");
    return this.#state;
  }

  #upgradeLevel(state: GameState, code: UpgradeCode): number {
    if (code === "precisionCutter") return state.upgrades.precisionCutterLevel;
    if (code === "mechanicalCutter") return state.upgrades.mechanicalCutterLevel;
    if (code === "energyCapacity") return state.upgrades.energyCapacityLevel;
    return state.upgrades.energyRecoveryLevel;
  }

  async #loadInviteUrl(): Promise<void> {
    const result = await this.#requestJson<{ url?: unknown }>(`${API_PREFIX}/invite-link`, { method: "GET" });
    if (typeof result.url !== "string") {
      this.#inviteUrl = "";
      return;
    }
    const invite = new URL(result.url);
    if (invite.protocol !== "https:" || invite.hostname !== "t.me" || !invite.searchParams.get("startapp")?.startsWith("ref_")) {
      throw new Error("Invalid Telegram Mini App invite link");
    }
    this.#inviteUrl = invite.toString();
  }

  async #loadPremiumOffers(): Promise<void> {
    const seasonId = this.#assertLoaded().season.id;
    const sequence = ++this.#premiumCatalogSequence;
    const result = await this.#requestJson<PremiumOfferResponse>(`${API_PREFIX}/premium/offers`, { method: "GET" });
    if (sequence !== this.#premiumCatalogSequence || this.#state?.season.id !== seasonId) return;
    this.#offerVersions.clear();
    if (!Array.isArray(result.offers)) throw new Error("Invalid premium catalogue response");
    const offers: NonNullable<GameState["premium"]["offerStates"]> = {};
    for (const raw of result.offers) {
      if (!isRecord(raw) || typeof raw.id !== "string" || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(raw.id)) continue;
      if (typeof raw.purchasedCount === "number" && Number.isSafeInteger(raw.purchasedCount) && raw.purchasedCount >= 0 && typeof raw.available === "boolean") {
        offers[raw.id] = { purchasedCount: raw.purchasedCount, available: raw.available };
      }
      if (raw.available === true && typeof raw.versionId === "string") this.#offerVersions.set(raw.id, raw.versionId);
    }
    this.#premiumOfferStates = { seasonId, offers };
    this.#state.premium = { ...this.#state.premium, offerStates: offers };
  }

  async #payInvoice(invoice: InvoiceResponse): Promise<void> {
    const invoiceId = stringValue(invoice.invoiceId, "invoice id");
    const invoiceLink = stringValue(invoice.invoiceLink, "invoice link");
    const telegram = this.#telegram;
    if (typeof telegram?.openInvoice !== "function") {
      throw new GameServiceError("INVALID_ACTION", "Telegram Stars are unavailable in this client");
    }
    const openInvoice = telegram.openInvoice.bind(telegram);
    const status = await new Promise<TelegramInvoiceStatus>((resolve, reject) => {
      try {
        openInvoice(invoiceLink, resolve);
      } catch (error) {
        reject(error instanceof Error ? error : new Error("Telegram invoice failed"));
      }
    });
    if (status === "cancelled" || status === "failed") {
      throw new GameServiceError("INVALID_ACTION", "Telegram Stars payment was not completed");
    }
    for (let attempt = 0; attempt < PAYMENT_POLL_ATTEMPTS; attempt += 1) {
      const purchase = await this.#requestJson<PremiumPurchaseResponse>(
        `${API_PREFIX}/premium/purchases/${encodeURIComponent(invoiceId)}`,
        { method: "GET" },
      );
      if (purchase.status === "PAID" || typeof purchase.purchaseId === "string") return;
      await this.#wait(PAYMENT_POLL_MS);
    }
    throw new GameServiceError("NETWORK", "Telegram Stars payment confirmation timed out");
  }

  #sendArenaCommand(
    command: ArenaActionCommand,
    accepts: ArenaPendingCommand["accepts"],
  ): Promise<ServiceEnvelope> {
    if (this.#arenaRecoveryPromise !== null) {
      return this.#arenaRecoveryPromise.then(() => accepts(this.#assertLoaded().pvp)
        ? this.#cachedEnvelope()
        : this.#sendArenaCommand(command, accepts));
    }
    if (this.#arenaPendingCommand !== null) {
      return Promise.reject(new GameServiceError("ARENA_SYNC", "Another Arena action is still pending"));
    }
    this.#connectArena();
    const connection = this.#arenaConnection;
    if (connection === null) {
      return Promise.reject(new GameServiceError("NETWORK", "Arena connection is unavailable"));
    }
    return new Promise<ServiceEnvelope>((resolve, reject) => {
      const timeoutId = globalThis.setTimeout(() => {
        void this.#recoverArenaCommandTimeout(command.commandId);
      }, ARENA_COMMAND_TIMEOUT_MS);
      this.#arenaPendingCommand = {
        commandId: command.commandId,
        dispatched: false,
        accepts,
        resolve,
        reject,
        timeoutId,
      };
      try {
        connection.send(command);
      } catch (error) {
        const pending = this.#takeArenaPendingCommand(command.commandId);
        pending?.reject(
          new GameServiceError("NETWORK", error instanceof Error ? error.message : "Arena command was not sent"),
        );
      }
    });
  }

  #takeArenaPendingCommand(commandId?: string): ArenaPendingCommand | null {
    const pending = this.#arenaPendingCommand;
    if (pending === null || (commandId !== undefined && pending.commandId !== commandId)) return null;
    globalThis.clearTimeout(pending.timeoutId);
    this.#arenaPendingCommand = null;
    return pending;
  }

  async #recoverArenaCommandTimeout(commandId: string): Promise<void> {
    const pending = this.#takeArenaPendingCommand(commandId);
    if (pending === null) return;
    try {
      const envelope = await this.#forceSync();
      this.#notify(envelope);
      if (pending.accepts(envelope.state.pvp)) {
        pending.resolve(envelope);
        return;
      }
      pending.reject(new GameServiceError("ARENA_SYNC", "Arena confirmation timed out"));
    } catch (error) {
      pending.reject(
        error instanceof GameServiceError
          ? error
          : new GameServiceError("NETWORK", "Arena recovery failed"),
      );
    }
  }

  async #recoverArenaProblem(event: ArenaProblemEvent): Promise<void> {
    if (this.#arenaRecoveryPromise !== null) {
      await this.#arenaRecoveryPromise;
      return;
    }
    this.#arenaRecoveryPromise = this.#performArenaProblemRecovery(event);
    let reconnect = false;
    try {
      reconnect = await this.#arenaRecoveryPromise;
    } finally {
      this.#arenaRecoveryPromise = null;
      if (reconnect) this.#connectArena();
    }
  }

  async #performArenaProblemRecovery(event: ArenaProblemEvent): Promise<boolean> {
    const pending = this.#takeArenaPendingCommand();
    try {
      if (event.code === "AUTH_REQUIRED") {
        this.#arenaConnection?.close();
        this.#arenaConnection = null;
        const delay = Math.max(0, this.#arenaAuthRetryAt - this.#now());
        if (delay > 0) await this.#wait(delay);
        if (this.#destroyed) throw new GameServiceError("NETWORK", "Arena connection closed");
        this.#arenaAuthRetryAt = this.#now() + Math.min(30_000, 500 * 2 ** Math.min(this.#arenaAuthFailures, 6));
        this.#arenaAuthFailures += 1;
        await this.#authenticate();
      }
      const envelope = await this.#forceSync();
      this.#notify(envelope);
      if (pending === null) return event.code === "AUTH_REQUIRED";
      if (pending.accepts(envelope.state.pvp)) {
        pending.resolve(envelope);
        return event.code === "AUTH_REQUIRED";
      }
      pending.reject(arenaProblemError(event));
      return event.code === "AUTH_REQUIRED";
    } catch (error) {
      pending?.reject(
        error instanceof GameServiceError
          ? error
          : new GameServiceError("NETWORK", "Arena recovery failed"),
      );
      return false;
    }
  }

  #handleArenaCommandDisposition(command: ArenaClientCommand, disposition: ArenaCommandDisposition): void {
    if (command.type === "arena.resume") return;
    const pending = this.#arenaPendingCommand;
    if (pending?.commandId !== command.commandId) return;
    if (disposition === "sent") {
      pending.dispatched = true;
      return;
    }
    const envelope = this.#cachedEnvelope();
    const superseded = this.#takeArenaPendingCommand(command.commandId);
    if (superseded?.accepts(envelope.state.pvp) === true) {
      superseded.resolve(envelope);
      return;
    }
    superseded?.reject(new GameServiceError("ARENA_SYNC", "Arena action was superseded"));
  }

  #connectArena(): void {
    if (this.#destroyed || this.#arenaRecoveryPromise !== null || this.#arenaConnection !== null || this.#accessToken === "") return;
    this.#arenaConnection = this.#arenaRealtime.connect({
      accessToken: this.#accessToken,
      getAccessToken: () => this.#accessToken,
      lastEventSequence: this.#state?.pvp.eventSequence ?? 0,
      onEvent: (event) => this.#handleArenaEvent(event),
      onCommandDisposition: (command, disposition) => this.#handleArenaCommandDisposition(command, disposition),
      onDisconnect: () => undefined,
    });
  }

  #handleArenaEvent(event: ArenaServerEvent): void {
    if (event.type === "arena.problem") {
      void this.#recoverArenaProblem(event);
      return;
    }
    if (this.#state === null) return;
    this.#arenaAuthFailures = 0;
    this.#arenaAuthRetryAt = 0;
    const incomingAt = Date.parse(event.serverNow);
    if (incomingAt < this.#lastArenaSnapshotAt || (
      event.match.matchId === this.#state.pvp.matchId && event.match.eventSequence < this.#state.pvp.eventSequence
    )) return;
    this.#lastArenaSnapshotAt = incomingAt;
    const accountIsCurrent = incomingAt >= Date.parse(this.#serverNow);
    const account = accountIsCurrent ? { ...this.#state.account, ...event.account } : this.#state.account;
    const changed = account.available !== this.#state.account.available ||
      account.reserved !== this.#state.account.reserved ||
      JSON.stringify(event.match) !== JSON.stringify(this.#state.pvp);
    const previousStatus = this.#state.pvp.status;
    this.#state = {
      ...this.#state,
      account,
      pvp: event.match,
    };
    if (accountIsCurrent) {
      this.#serverNow = event.serverNow;
      this.#serverAnchor = this.#now();
    }
    const envelope = { state: this.#state, serverNow: this.#serverNow };
    if (changed) this.#notify(envelope);
    const pending = this.#arenaPendingCommand;
    if (pending?.dispatched === true && pending.accepts(event.match)) {
      this.#takeArenaPendingCommand(pending.commandId)?.resolve(envelope);
    }
    if (changed && (event.match.status === "result" || (previousStatus !== "idle" && event.match.status === "idle"))) {
      void this.#forceSync().then((envelope) => this.#notify(envelope)).catch(() => undefined);
    }
  }

  #notify(envelope: ServiceEnvelope): void {
    const synchronized = { ...envelope, pendingTaps: this.#pendingTapSummary() };
    for (const listener of this.#listeners) listener(synchronized);
  }
}
